/**
 * ⚙️ **소품 자동 맞춤** — 그림을 재서 놓을 자리를 계산한다.
 *
 * ## 왜 필요한가
 * 운영자가 「직접 만들기」로 올린 모자·소품(c5~c8·a5~a8)은 **아이템 기본값이 없다**.
 * 그러면 그림을 프레임 가득 그대로 얹는데, 우리 그림과 여백·크기가 다르면 어긋난다 —
 * 모자가 얼굴을 덮거나, 가방이 몸에서 떨어져 뜬다(2026-08-22 신고).
 *
 * 손조절 화면은 이미 있다. 문제는 **처음 값이 아무 근거가 없다**는 것이다.
 * 여기서는 **그림에서 잰 사실**로 첫 값을 만든다 — 그 다음은 운영자가 다듬는다.
 *
 * ## 무엇을 재나
 * 투명하지 않은 부분의 사각형(bbox)만 있으면 된다. 브라우저 canvas 로 잴 수 있고,
 * 그림 안에 여백이 얼마나 있는지·물건이 실제로 얼마나 큰지가 거기서 나온다.
 *
 * ## 어디에 붙이나 (물건 종류마다 다르다)
 * - **모자·머리장식** → 머리 위에. 그림의 **아랫변 가운데**가 머리 꼭대기에 앉는다
 * - **가슴 소품**(배지·목걸이) → 가슴에. 그림의 **한가운데**가 가슴에 온다
 * - **쥐는 물건**(랜턴·가방) → 손에. 그림의 **윗변 가운데**(손잡이)가 손에 온다
 *
 * 같은 「소품」이라도 배지와 랜턴은 붙는 곳이 다르다 — 그래서 종류를 받는다.
 *
 * ## 이 값은 **제안**이다
 * 비대칭 물건·기울어진 그림까지 맞히지는 못한다. 그래서 덮어쓰지 않고, 운영자가 보고
 * 마음에 안 들면 손조절이 이긴다. 자동값을 정답처럼 굳히면 고칠 방법이 없어진다.
 */
import type { HeroAnchor } from "./heroAnchor.ts";
import type { OverlayFit } from "./heroOverlayFit.ts";

/** 그림에서 잰 것 — 프레임 한 변 대비 비율(0~1). */
export interface ArtBox {
  /** 투명하지 않은 부분의 왼쪽·위·폭·높이. */
  x: number;
  y: number;
  w: number;
  h: number;
}

/** 어디에 붙는 물건인가. */
export type AttachKind = "head" | "chest" | "hand";

/**
 * 아래 숫자는 **지어낸 값이 아니다.** 사람이 손으로 맞춰 둔 c1~c4·a1~a3 이
 * 실제로 어디에 얼마 크기로 앉는지 역산해서 뽑았다(2026-08-22 실측).
 *
 * 손조절값 그 자체가 "이렇게 보이면 맞다"의 정답지다 — 그걸 재현하는 규칙이면
 * 새 그림에도 그럴듯한 첫 값을 준다.
 */
/** 모자 폭 ÷ 머리 폭. c1~c4 실측 1.085~1.127 의 가운데. */
export const HEAD_W_RATIO = 1.1;
/**
 * 모자 **아랫변**이 앉는 높이 — 머리 세로 한가운데에서 머리 폭의 몇 배만큼 아래인가.
 * c1~c4 실측 0.144~0.298, 평균 0.215. (머리 꼭대기가 아니라 이마·눈 언저리다 —
 * 「꼭대기」로 잡았다가 프레임 밖으로 나가 전부 잘렸다.)
 */
export const HEAD_BOTTOM_BELOW_MID = 0.215;

/** 가슴 소품 폭 ÷ 머리 폭. 배지(a2) 실측 0.241. */
export const CHEST_W_RATIO = 0.24;
/** 가슴 소품 **한가운데**가 턱밑 기준점보다 얼마나 아래인가(머리 폭 대비). a2 실측 0.349. */
export const CHEST_BELOW_ANCHOR = 0.35;

/** 쥐는 물건 폭 ÷ 머리 폭. 랜턴(a3) 실측 0.358. */
export const HAND_W_RATIO = 0.36;
/** 쥐는 물건 **한가운데**가 손 기준점보다 얼마나 위인가(머리 폭 대비). a3 실측 -0.099. */
export const HAND_ABOVE_ANCHOR = 0.1;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * 잰 그림 + 기준 아이 좌표 → 놓을 자리.
 *
 * ## 수식이 왜 이런가
 * 두 렌더러는 오버레이를 **프레임 중심 기준으로 `scale` 배 → `(dx,dy)` 이동** 시킨다.
 * 그림 안의 점 `q`(중심 기준 비율)는 `s·q + d` 로 간다. 우리는 "그림의 어느 점이 몸의 어느 점에
 * 가야 하는지"를 아니까, 그 둘을 같게 두고 `d` 를 푼다:
 *
 *   s·q + d = target   →   d = target − s·q
 *
 * `q` 는 물건 종류가 정한다(모자=아랫변 가운데, 가슴=한가운데, 손=윗변 가운데).
 */
export function autoOverlayFit(box: ArtBox, kind: AttachKind, anchor: HeroAnchor): OverlayFit {
  // 그림이 비어 있으면(전부 투명) 손댈 근거가 없다 — 중립으로 둔다
  if (!(box.w > 0) || !(box.h > 0)) return { scale: 1, dx: 0, dy: 0, rot: 0, flipX: false };

  // ① 얼마나 키울까 — 물건의 **실제 폭**이 목표 폭이 되도록
  const ratio = kind === "head" ? HEAD_W_RATIO : kind === "chest" ? CHEST_W_RATIO : HAND_W_RATIO;
  const scale = clamp((anchor.headW * ratio) / box.w, 0.3, 1.2); // 손조절 화면과 같은 범위

  // ② 그림의 어느 점을 붙일까(프레임 중심 기준 비율)
  const cx = box.x + box.w / 2 - 0.5;
  const q =
    kind === "head"
      ? { x: cx, y: box.y + box.h - 0.5 } // 아랫변 가운데 = 모자 테두리
      : { x: cx, y: box.y + box.h / 2 - 0.5 }; // 한가운데

  // ③ 몸의 어느 점에 가야 하나(프레임 중심 기준 비율)
  const target =
    kind === "head"
      ? {
          x: anchor.headCx - 0.5,
          y: anchor.headMidY + anchor.headW * HEAD_BOTTOM_BELOW_MID - 0.5,
        }
      : kind === "chest"
        ? { x: anchor.chestCx - 0.5, y: anchor.chestY + anchor.headW * CHEST_BELOW_ANCHOR - 0.5 }
        : { x: anchor.handCx - 0.5, y: anchor.handY - anchor.headW * HAND_ABOVE_ANCHOR - 0.5 };

  return {
    scale,
    dx: clamp(target.x - scale * q.x, -0.25, 0.25), // 손조절 화면과 같은 상한
    dy: clamp(target.y - scale * q.y, -0.25, 0.25),
    rot: 0,
    flipX: false,
  };
}

/**
 * 키 이름만 보고 어디에 붙는 물건인지 짐작한다.
 *
 * 「모자」는 이름으로 알 수 있지만(`hero.hair.*`) **소품은 가슴인지 손인지 이름으로 못 가른다**.
 * 그래서 짐작은 `chest` 로 두고 — 배지·목걸이가 더 흔하다 — 손에 쥐는 물건은 운영자가 고르게 한다.
 * 틀린 짐작을 조용히 쓰는 것보다 **고를 수 있게** 하는 편이 낫다.
 */
export function guessAttachKind(key: string): AttachKind {
  return key.startsWith("hero.hair.") ? "head" : "chest";
}
